(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeMenu = null;

  const getFocusableItems = (panel) => {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(focusableSelector));
  };

  const closeMenu = (menu, restoreFocus = true) => {
    if (!menu?.toggle || !menu?.overlay) return;

    menu.toggle.setAttribute('aria-expanded', 'false');
    menu.overlay.classList.remove('is-open');
    menu.overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');

    if (activeMenu === menu) {
      activeMenu = null;
    }

    if (restoreFocus && menu.previousFocus && typeof menu.previousFocus.focus === 'function') {
      menu.previousFocus.focus();
    }
  };

  const openMenu = (menu) => {
    if (!menu?.toggle || !menu?.overlay) return;

    if (activeMenu && activeMenu !== menu) {
      closeMenu(activeMenu, false);
    }

    menu.previousFocus = document.activeElement;
    menu.toggle.setAttribute('aria-expanded', 'true');
    menu.overlay.classList.add('is-open');
    menu.overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    activeMenu = menu;

    const focusableItems = getFocusableItems(menu.panel);
    if (focusableItems.length) focusableItems[0].focus();
  };

  const initHeader = (root = document) => {
    root.querySelectorAll('.site-header').forEach((header) => {
      if (header.dataset.headerReady === 'true') return;

      const menu = {
        root: header,
        toggle: header.querySelector('[data-menu-toggle]'),
        overlay: header.querySelector('[data-menu-overlay]'),
        panel: header.querySelector('[data-menu-panel]'),
        close: header.querySelector('[data-menu-close]'),
        previousFocus: null
      };

      if (!menu.toggle || !menu.overlay || !menu.panel) return;

      header.dataset.headerReady = 'true';
      menu.toggle.addEventListener('click', () => openMenu(menu));
      menu.close?.addEventListener('click', () => closeMenu(menu));

      menu.overlay.addEventListener('click', (event) => {
        if (event.target?.hasAttribute('data-menu-dismiss')) closeMenu(menu);
      });

      header.querySelectorAll('.site-header__dropdown').forEach((dropdown) => {
        dropdown.addEventListener('mouseenter', () => {
          if (window.matchMedia('(min-width: 64rem)').matches) dropdown.setAttribute('open', '');
        });

        dropdown.addEventListener('mouseleave', () => {
          if (window.matchMedia('(min-width: 64rem)').matches) dropdown.removeAttribute('open');
        });
      });

      if (header.hasAttribute('data-transparent-header')) {
        const updateHeaderState = () => {
          header.classList.toggle('is-scrolled', window.scrollY > 40);
        };

        updateHeaderState();
        window.addEventListener('scroll', updateHeaderState, { passive: true });
      }
    });
  };

  const trapFocus = (event) => {
    if (event.key !== 'Tab' || !activeMenu?.overlay?.classList.contains('is-open')) return;

    const focusableItems = getFocusableItems(activeMenu.panel);
    if (!focusableItems.length) return;

    const firstItem = focusableItems[0];
    const lastItem = focusableItems[focusableItems.length - 1];

    if (event.shiftKey && document.activeElement === firstItem) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && document.activeElement === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  };

  const initAnimations = (root = document) => {
    const animatedItems = root.querySelectorAll('[data-animate]:not(.is-observed)');
    if (!animatedItems.length) return;

    if (!('IntersectionObserver' in window)) {
      animatedItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

    animatedItems.forEach((item) => {
      item.classList.add('is-observed');
      observer.observe(item);
    });
  };

  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-collection-carousel]').forEach((track) => {
      if (track.dataset.carouselReady === 'true') return;

      const section = track.closest('.collection-carousel');
      const prev = section?.querySelector('[data-carousel-prev]');
      const next = section?.querySelector('[data-carousel-next]');
      const viewport = section?.querySelector('.collection-carousel__viewport');
      let autoplayTimer = null;

      if (!section || (!prev && !next)) return;

      const getScrollDistance = () => {
        const card = track.querySelector('.collection-carousel__card');
        const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      };

      const goToNext = () => {
        const distance = getScrollDistance();
        const maxScroll = track.scrollWidth - track.clientWidth;

        if (track.scrollLeft + distance >= maxScroll - 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        track.scrollBy({ left: distance, behavior: 'smooth' });
      };

      const goToPrev = () => {
        const distance = getScrollDistance();
        const maxScroll = track.scrollWidth - track.clientWidth;

        if (track.scrollLeft <= 2) {
          track.scrollTo({ left: maxScroll, behavior: 'smooth' });
          return;
        }

        track.scrollBy({ left: -distance, behavior: 'smooth' });
      };

      const stopAutoplay = () => {
        if (!autoplayTimer) return;
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (track.scrollWidth <= track.clientWidth) return;

        autoplayTimer = window.setInterval(goToNext, 3600);
      };

      prev?.addEventListener('click', () => {
        stopAutoplay();
        goToPrev();
        startAutoplay();
      });

      next?.addEventListener('click', () => {
        stopAutoplay();
        goToNext();
        startAutoplay();
      });

      section.addEventListener('mouseenter', stopAutoplay);
      section.addEventListener('mouseleave', startAutoplay);
      section.addEventListener('focusin', stopAutoplay);
      section.addEventListener('focusout', (event) => {
        if (!section.contains(event.relatedTarget)) startAutoplay();
      });

      viewport?.addEventListener('touchstart', stopAutoplay, { passive: true });
      viewport?.addEventListener('touchend', startAutoplay, { passive: true });
      window.addEventListener('resize', startAutoplay);

      track.dataset.carouselReady = 'true';
      startAutoplay();
    });
  };

  const initProductGrids = (root = document) => {
    root.querySelectorAll('[data-shop-products]').forEach((section) => {
      if (section.dataset.productsReady === 'true') return;

      const grid = section.querySelector('[data-product-grid]');
      const cards = grid ? Array.from(grid.querySelectorAll('[data-product-card]')) : [];
      const filterButtons = Array.from(section.querySelectorAll('[data-product-filter]'));
      const sortSelect = section.querySelector('[data-product-sort]');
      const emptyState = section.querySelector('[data-product-empty]');
      let activeFilter = 'all';

      if (!grid || !cards.length) return;

      const matchesFilter = (card) => {
        const price = Number(card.dataset.price || 0);
        const isSale = card.dataset.sale === 'true';
        const isAvailable = card.dataset.available === 'true';

        if (activeFilter === 'sale') return isSale;
        if (activeFilter === 'available') return isAvailable;
        if (activeFilter === 'under-50') return price < 5000;
        if (activeFilter === 'over-50') return price >= 5000;
        return true;
      };

      const sortCards = () => {
        const sortValue = sortSelect?.value || 'featured';
        const sortedCards = [...cards].sort((a, b) => {
          if (sortValue === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
          if (sortValue === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
          if (sortValue === 'newest') return Number(b.dataset.created) - Number(a.dataset.created);
          return Number(a.dataset.index) - Number(b.dataset.index);
        });

        sortedCards.forEach((card) => grid.appendChild(card));
      };

      const renderCards = () => {
        let visibleCount = 0;
        sortCards();

        cards.forEach((card) => {
          const isVisible = matchesFilter(card);
          card.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (emptyState) emptyState.hidden = visibleCount > 0;
      };

      filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          activeFilter = button.dataset.productFilter || 'all';

          filterButtons.forEach((item) => {
            const isActive = item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', String(isActive));
          });

          renderCards();
        });
      });

      sortSelect?.addEventListener('change', renderCards);
      section.dataset.productsReady = 'true';
      renderCards();
    });
  };

  const initProductAccordions = (root = document) => {
    root.querySelectorAll('[data-product-accordion]').forEach((accordion) => {
      if (accordion.dataset.accordionReady === 'true') return;

      const summary = accordion.querySelector('summary');
      if (!summary) return;

      const syncState = () => {
        summary.setAttribute('aria-expanded', String(accordion.open));
      };

      accordion.addEventListener('toggle', syncState);
      accordion.dataset.accordionReady = 'true';
      syncState();
    });
  };

  const initProductVariantPickers = (root = document) => {
    root.querySelectorAll('[data-product-variant-picker]').forEach((picker) => {
      if (picker.dataset.variantPickerReady === 'true') return;

      const form = picker.closest('form');
      const productPage = picker.closest('.product-page');
      const select = picker.querySelector('[data-product-variant-select]');
      const groups = Array.from(picker.querySelectorAll('[data-product-option-group]'));
      const buttons = Array.from(picker.querySelectorAll('[data-product-option-value]'));
      const price = productPage?.querySelector('[data-product-price]');
      const availability = productPage?.querySelector('.product-page__availability');
      const submit = form?.querySelector('.product-page__submit');

      if (!select || !groups.length || !buttons.length) return;

      const variants = Array.from(select.options).map((option) => {
        let options = [];

        try {
          options = JSON.parse(option.dataset.options || '[]');
        } catch (error) {
          options = [];
        }

        return {
          id: option.value,
          available: option.dataset.available === 'true',
          options,
          price: option.dataset.price || '',
          comparePrice: option.dataset.comparePrice || '',
          mediaId: option.dataset.mediaId || ''
        };
      });

      const selectedOptions = groups.map((group) => {
        const selected = group.querySelector('[data-product-option-value].is-selected');
        return selected?.dataset.optionValue || group.querySelector('[data-product-option-value]')?.dataset.optionValue || '';
      });

      const getVariant = (options) => variants.find((variant) => (
        variant.options.length === options.length && variant.options.every((value, index) => value === options[index])
      ));

      const hasAvailableVariantForValue = (optionIndex, value) => variants.some((variant) => (
        variant.available && variant.options.every((variantValue, index) => {
          if (groups[optionIndex]?.dataset.optionKind === 'color') {
            return index === optionIndex ? variantValue === value : true;
          }

          if (index === optionIndex) return variantValue === value;
          return !selectedOptions[index] || variantValue === selectedOptions[index];
        })
      ));

      const updateOptionStates = () => {
        groups.forEach((group, groupIndex) => {
          const selectedLabel = group.querySelector('[data-selected-option-label]');
          if (selectedLabel) selectedLabel.textContent = selectedOptions[groupIndex] || '';

          group.querySelectorAll('[data-product-option-value]').forEach((button) => {
            const value = button.dataset.optionValue || '';
            const isSelected = selectedOptions[groupIndex] === value;
            const isAvailable = hasAvailableVariantForValue(groupIndex, value);

            button.classList.toggle('is-selected', isSelected);
            button.classList.toggle('is-unavailable', !isAvailable);
            button.disabled = !isAvailable;
            button.setAttribute('aria-pressed', String(isSelected));
          });
        });
      };

      const updateProductDetails = (variant) => {
        if (!variant) return;

        select.value = variant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        if (price) {
          price.innerHTML = variant.comparePrice
            ? `<span>${variant.price}</span><s>${variant.comparePrice}</s>`
            : `<span>${variant.price}</span>`;
        }

        if (availability) {
          availability.textContent = variant.available ? 'Disponible' : 'Indisponible';
        }

        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? 'Ajouter au panier' : 'Indisponible';
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        if (variant.mediaId) {
          const media = productPage?.querySelector(`[data-product-media-id="${variant.mediaId}"]`);

          if (media) {
            productPage.querySelectorAll('[data-product-media-id]').forEach((item) => {
              item.classList.toggle('is-main', item === media);
            });

            media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }
      };

      const selectVariant = (nextOptions) => {
        let variant = getVariant(nextOptions);

        if (!variant || !variant.available) {
          variant = variants.find((item) => (
            item.available && item.options.every((value, index) => !nextOptions[index] || value === nextOptions[index])
          )) || variant;
        }

        if (!variant) return;

        variant.options.forEach((value, index) => {
          selectedOptions[index] = value;
        });

        updateOptionStates();
        updateProductDetails(variant);
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;

          const optionIndex = Number(button.dataset.optionPosition || 1) - 1;
          const nextOptions = [...selectedOptions];
          nextOptions[optionIndex] = button.dataset.optionValue || '';
          selectVariant(nextOptions);
        });
      });

      picker.dataset.variantPickerReady = 'true';
      updateOptionStates();
    });
  };

  const initProductGalleries = (root = document) => {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;

      const track = gallery.querySelector('[data-product-media-track]');
      const prev = gallery.querySelector('[data-product-gallery-prev]');
      const next = gallery.querySelector('[data-product-gallery-next]');
      const thumbs = Array.from(gallery.querySelectorAll('.product-page__thumb'));

      if (!track) return;

      const getScrollDistance = () => {
        const item = track.querySelector('.product-page__media-item');
        const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0');
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      };

      const scrollGallery = (direction) => {
        track.scrollBy({
          left: getScrollDistance() * direction,
          behavior: 'smooth'
        });
      };

      prev?.addEventListener('click', () => scrollGallery(-1));
      next?.addEventListener('click', () => scrollGallery(1));

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          const targetId = thumb.getAttribute('href');
          const target = targetId ? gallery.querySelector(targetId) : null;

          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        });
      });

      gallery.dataset.galleryReady = 'true';
    });
  };

  const initProductLightboxes = (root = document) => {
    root.querySelectorAll('[data-product-lightbox]').forEach((lightbox) => {
      if (lightbox.dataset.lightboxReady === 'true') return;

      const section = lightbox.closest('.product-page');
      const image = lightbox.querySelector('[data-product-lightbox-image]');
      const closeButtons = lightbox.querySelectorAll('[data-product-lightbox-close]');
      const triggers = section ? section.querySelectorAll('[data-product-lightbox-trigger]') : [];
      let previousFocus = null;

      if (!section || !image || !triggers.length) return;

      const closeLightbox = () => {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('menu-open');
        image.removeAttribute('src');

        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
      };

      const openLightbox = (trigger) => {
        previousFocus = document.activeElement;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = trigger.dataset.zoomSrc || '';
        image.alt = trigger.dataset.zoomAlt || '';
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('menu-open');
        lightbox.querySelector('[data-product-lightbox-close]')?.focus();
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => openLightbox(trigger));
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', closeLightbox);
      });

      lightbox.dataset.lightboxReady = 'true';
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeMenu?.overlay?.classList.contains('is-open')) {
      closeMenu(activeMenu);
    }

    if (event.key === 'Escape') {
      document.querySelectorAll('[data-product-lightbox].is-open').forEach((lightbox) => {
        lightbox.querySelector('[data-product-lightbox-close]')?.click();
      });
    }

    trapFocus(event);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initHeader(event.target);
    initAnimations(event.target);
    initCarousels(event.target);
    initProductGrids(event.target);
    initProductAccordions(event.target);
    initProductVariantPickers(event.target);
    initProductGalleries(event.target);
    initProductLightboxes(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (activeMenu?.root && event.target.contains(activeMenu.root)) {
      closeMenu(activeMenu, false);
    }
  });

  initHeader();
  initAnimations();
  initCarousels();
  initProductGrids();
  initProductAccordions();
  initProductVariantPickers();
  initProductGalleries();
  initProductLightboxes();
})();
